import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Button,
    Typography,
    Box,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Stack,
    useTheme,
    useMediaQuery,
    alpha,
    Chip,
    Fab,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import useSettings from '../../../hooks/useSettings';
import useTrade from '../../../hooks/useTrade';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import modifyScriptName from '../../../utils/modifyScriptName';
import { getInstrumentList } from '../../../redux/slices/trades';
import HedgingWatchlistModal from './HedgingWatchlistModal';

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    fontSize: '0.8rem',
    fontWeight: 500,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    '&.header': {
        backgroundColor: '#f8f9fa',
        fontWeight: 600,
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        fontSize: '0.85rem',
        textAlign: 'center',
    }
}));

const CompactCard = styled(Card)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
    boxShadow: 'none',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
}));

const MobileCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(0.75),
    borderRadius: 6,
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    boxShadow: 'none',
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
}));

const CompactRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.75, 1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minHeight: 48,
}));

const ScriptCompact = styled(Box)(({ theme, side }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(0.5),
    borderRadius: 4,
    backgroundColor: side === 'BUY'
        ? alpha(theme.palette.success.main, 0.06)
        : alpha(theme.palette.error.main, 0.06),
    border: `1px solid ${side === 'BUY'
        ? alpha(theme.palette.success.main, 0.15)
        : alpha(theme.palette.error.main, 0.15)
        }`,
}));

const MiniChip = styled(Chip)(({ theme, side }) => ({
    height: 16,
    fontSize: '0.55rem',
    fontWeight: 700,
    backgroundColor: side === 'BUY'
        ? theme.palette.success.main
        : theme.palette.error.main,
    color: 'white',
    borderRadius: '2px',
    minWidth: 28,
    '& .MuiChip-label': {
        padding: '0 4px',
    },
}));

const ScriptBox = styled(Box)(({ theme, side }) => ({
    padding: theme.spacing(0.75, 1),
    borderRadius: 6,
    backgroundColor: side === 'BUY'
        ? alpha(theme.palette.success.main, 0.08)
        : alpha(theme.palette.error.main, 0.08),
    border: `1px solid ${side === 'BUY'
        ? alpha(theme.palette.success.main, 0.2)
        : alpha(theme.palette.error.main, 0.2)
        }`,
    minHeight: 50,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
}));

const HedgingWatchList = () => {
    const { themeStretch } = useSettings();
    const theme = useTheme();
    const location = useLocation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // WebSocket for real-time updates
    const { localSocket } = useTrade();

    // Redux state
    const quotesData = useSelector((state) => state?.trade?.quotesData || {});
    const previousQuote = useSelector((state) => state?.trade?.previousQuotesData || {});

    // Local state
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [hedgingWatchlist, setHedgingWatchlist] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Load watchlist from localStorage on component mount
    useEffect(() => {
        const savedWatchlist = localStorage.getItem('hedgingWatchlist');
        if (savedWatchlist) {
            try {
                const parsedWatchlist = JSON.parse(savedWatchlist);
                setHedgingWatchlist(parsedWatchlist);
            } catch (error) {
                console.error('Error parsing hedging watchlist from localStorage:', error);
                setHedgingWatchlist([]);
            }
        }
    }, []);

    // Real-time subscription for watchlist instruments
    useEffect(() => {
        if (!hedgingWatchlist || hedgingWatchlist.length === 0 || !localSocket) return;

        // Extract instrument tokens from both scripts
        const instrumentTokens = [];
        hedgingWatchlist.forEach(item => {
            if (item.script1?.instrument_token) {
                instrumentTokens.push(parseInt(item.script1.instrument_token, 10));
            }
            if (item.script2?.instrument_token) {
                instrumentTokens.push(parseInt(item.script2.instrument_token, 10));
            }
        });

        // Remove duplicates
        const uniqueTokens = [...new Set(instrumentTokens)];

        if (uniqueTokens.length === 0) return;

        const sendSubscribeMessage = () => {
            if (localSocket && localSocket.readyState === WebSocket.OPEN) {
                localSocket.send(JSON.stringify({
                    event: 'subscribe',
                    data: uniqueTokens
                }));
                // console.log('Subscribed to hedging watchlist instruments:', uniqueTokens);
            }
        };

        // Subscribe immediately if connection is open, or when it opens
        if (localSocket.readyState === WebSocket.OPEN) {
            setTimeout(sendSubscribeMessage, 100);
        } else {
            const originalOnOpen = localSocket.onopen;
            localSocket.onopen = (event) => {
                if (originalOnOpen) originalOnOpen.call(localSocket, event);
                setTimeout(sendSubscribeMessage, 100);
            };
        }

        // Cleanup: unsubscribe when component unmounts or dependencies change
        return () => {
            if (localSocket && localSocket.readyState === WebSocket.OPEN && uniqueTokens.length > 0) {
                localSocket.send(JSON.stringify({
                    event: 'unsubscribe',
                    data: uniqueTokens
                }));
                // console.log('Unsubscribed from hedging watchlist instruments:', uniqueTokens);
            }
        };
    }, [hedgingWatchlist, localSocket]);

    // Get real-time quote data with BID/ASK logic
    const getQuoteData = (instrumentToken, side) => {
        if (!instrumentToken || !quotesData[instrumentToken]) {
            return { price: 0, change: 0, changePercent: 0, bid: 0, ask: 0, ltp: 0 };
        }

        const current = quotesData[instrumentToken];
        const previous = previousQuote[instrumentToken];

        // Get BID and ASK prices from multiple possible sources
        const bid = current.depth?.buy?.[0]?.price ||
            current.bid ||
            current.buy_price ||
            current.ltp ||
            current.last_price ||
            0;

        const ask = current.depth?.sell?.[0]?.price ||
            current.ask ||
            current.sell_price ||
            current.offer ||
            current.ltp ||
            current.last_price ||
            0;

        // Select price based on side: BUY uses BID, SELL uses ASK
        const currentPrice = side === 'BUY' ? bid : ask;

        // For change calculation, use LTP as reference
        const ltp = current.ltp || current.last_price || currentPrice;
        const prevClose = previous?.close || current.ohlc?.close || ltp;
        const change = ltp - prevClose;
        const changePercent = prevClose !== 0 ? (change / prevClose) * 100 : 0;

        return {
            price: currentPrice,
            change,
            changePercent,
            bid,
            ask,
            ltp
        };
    };

    // Format price
    const formatPrice = (price) => {
        if (!price || Number.isNaN(Number(price))) return '0.00';
        return parseFloat(price).toFixed(2);
    };

    // Get price color
    const getPriceColor = (change) => {
        if (change > 0) return theme.palette.success.main;
        if (change < 0) return theme.palette.error.main;
        return theme.palette.text.primary;
    };

    // Get side chip
    const getSideChip = (side, mini = false) => (
        mini ? (
            <MiniChip
                label={side}
                size="small"
                variant="filled"
                side={side}
            />
        ) : (
            <Chip
                label={side}
                size="small"
                variant="filled"
                sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    backgroundColor: side === 'BUY'
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                    color: 'white',
                    borderRadius: '3px',
                    minWidth: 32,
                    '& .MuiChip-label': {
                        px: 0.5,
                        py: 0,
                    },
                }}
            />
        )
    );

    // Filter watchlist based on search
    const filteredWatchlist = hedgingWatchlist.filter(item => {
        if (!searchText) return true;
        const searchLower = searchText.toLowerCase();
        const script1Name = modifyScriptName(item.script1?.name, item.script1?.expiry) || '';
        const script2Name = modifyScriptName(item.script2?.name, item.script2?.expiry) || '';
        return script1Name.toLowerCase().includes(searchLower) ||
            script2Name.toLowerCase().includes(searchLower);
    });

    // Optimize for mobile - show maximum 10 items at once for better performance
    const displayedWatchlist = isMobile ? filteredWatchlist.slice(0, 20) : filteredWatchlist;

    // Handle adding new hedging pair
    const handleAddHedgingPair = () => {
        setShowAddModal(true);
    };

    // Handle modal close and refresh
    const handleCloseModal = () => {
        setShowAddModal(false);
        // Refresh watchlist from localStorage
        const savedWatchlist = localStorage.getItem('hedgingWatchlist');
        if (savedWatchlist) {
            try {
                const parsedWatchlist = JSON.parse(savedWatchlist);
                setHedgingWatchlist(parsedWatchlist);
            } catch (error) {
                console.error('Error parsing hedging watchlist:', error);
            }
        }
    };

    // Handle edit mode toggle
    const handleEditModeToggle = () => {
        setEditMode(!editMode);
        setSelectedItems([]);
    };

    // Handle item selection
    const handleItemSelect = (itemId) => {
        setSelectedItems(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    // Handle select all
    const handleSelectAll = () => {
        if (selectedItems.length === displayedWatchlist.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(displayedWatchlist.map(item => item.id));
        }
    };

    // Handle delete selected
    const handleDeleteSelected = () => {
        if (selectedItems.length === 0) return;
        setDeleteDialogOpen(true);
    };

    // Confirm delete
    const confirmDelete = () => {
        const updatedWatchlist = hedgingWatchlist.filter(item => !selectedItems.includes(item.id));
        setHedgingWatchlist(updatedWatchlist);
        localStorage.setItem('hedgingWatchlist', JSON.stringify(updatedWatchlist));
        setSelectedItems([]);
        setEditMode(false);
        setDeleteDialogOpen(false);
        enqueueSnackbar(`${selectedItems.length} items deleted successfully`, { variant: 'success' });
    };

    // Calculate hedging difference using BID/ASK prices
    const calculateHedgingDifference = (script1, script2) => {
        const quote1 = getQuoteData(script1?.instrument_token, script1?.side);
        const quote2 = getQuoteData(script2?.instrument_token, script2?.side);

        // Use the appropriate price based on side:
        // - BUY side uses BID price (price at which you can sell)
        // - SELL side uses ASK price (price at which you can buy)
        const price1 = quote1.price || 0;
        const price2 = quote2.price || 0;
        const multiplier1 = script1?.multiplier === undefined ? 1 : script1?.multiplier;
        const multiplier2 = script2?.multiplier === undefined ? 1 : script2?.multiplier;
        // Calculate difference: Script1 price - Script2 price
        // This shows the spread between the two hedging positions
        const difference = (price1 * multiplier1) - (price2 * multiplier2);

        return {
            difference: formatPrice(Math.abs(difference)), // Show absolute value for clarity
            rawDifference: difference, // Keep raw value for color determination
            color: getPriceColor(difference)
        };
    };

    // Render desktop table
    const renderDesktopTable = () => (
        <CompactCard>
            <Scrollbar>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {editMode && (
                                    <StyledTableCell className="header">
                                        <Checkbox
                                            checked={selectedItems.length === displayedWatchlist.length && displayedWatchlist.length > 0}
                                            indeterminate={selectedItems.length > 0 && selectedItems.length < displayedWatchlist.length}
                                            onChange={handleSelectAll}
                                            size="small"
                                        />
                                    </StyledTableCell>
                                )}
                                <StyledTableCell className="header">Script 1</StyledTableCell>
                                <StyledTableCell className="header">Script 2</StyledTableCell>
                                <StyledTableCell className="header">Hedging Difference</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedWatchlist.map((item) => {
                                const quote1 = getQuoteData(item.script1?.instrument_token, item.script1?.side);
                                const quote2 = getQuoteData(item.script2?.instrument_token, item.script2?.side);
                                const hedgingDiff = calculateHedgingDifference(item.script1, item.script2);

                                return (
                                    <TableRow key={item.id}>
                                        {editMode && (
                                            <StyledTableCell>
                                                <Checkbox
                                                    checked={selectedItems.includes(item.id)}
                                                    onChange={() => handleItemSelect(item.id)}
                                                    size="small"
                                                />
                                            </StyledTableCell>
                                        )}
                                        <StyledTableCell>
                                            <ScriptBox side={item.script1.side}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.25 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.2 }}>
                                                        {modifyScriptName(item.script1.name, item.script1.expiry)}
                                                    </Typography>
                                                    {getSideChip(item.script1.side)}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                                    <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, color: getPriceColor(quote1.change), lineHeight: 1.2 }}>
                                                        {formatPrice(quote1.price)}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        color: item.script1.side === 'BUY' ? 'success.dark' : 'error.dark',
                                                        backgroundColor: item.script1.side === 'BUY' ? 'success.lighter' : 'error.lighter',
                                                        px: 0.4,
                                                        py: 0.15,
                                                        borderRadius: 0.4,
                                                        lineHeight: 1
                                                    }}>
                                                        {item.script1.side === 'BUY' ? 'BID' : 'ASK'}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.2 }}>
                                                    {quote1.change > 0 ? '+' : ''}{formatPrice(quote1.change)} ({quote1.changePercent > 0 ? '+' : ''}{quote1.changePercent.toFixed(2)}%)
                                                </Typography>
                                            </ScriptBox>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <ScriptBox side={item.script2.side}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.25 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.2 }}>
                                                        {modifyScriptName(item.script2.name, item.script2.expiry)}
                                                    </Typography>
                                                    {getSideChip(item.script2.side)}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                                    <Typography variant="h6" sx={{ fontSize: '0.8rem', fontWeight: 600, color: getPriceColor(quote2.change), lineHeight: 1.2 }}>
                                                        {formatPrice(quote2.price)}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        color: item.script2.side === 'BUY' ? 'success.dark' : 'error.dark',
                                                        backgroundColor: item.script2.side === 'BUY' ? 'success.lighter' : 'error.lighter',
                                                        px: 0.4,
                                                        py: 0.15,
                                                        borderRadius: 0.4,
                                                        lineHeight: 1
                                                    }}>
                                                        {item.script2.side === 'BUY' ? 'BID' : 'ASK'}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.2 }}>
                                                    {quote2.change > 0 ? '+' : ''}{formatPrice(quote2.change)} ({quote2.changePercent > 0 ? '+' : ''}{quote2.changePercent.toFixed(2)}%)
                                                </Typography>
                                            </ScriptBox>
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Box sx={{ padding: theme.spacing(1.5), textAlign: 'center' }}>
                                                <Typography variant="h5" sx={{ fontSize: '1.1rem', fontWeight: 700, color: hedgingDiff.color, mb: 0.5 }}>
                                                    {hedgingDiff.rawDifference >= 0 ? '+' : '-'}{hedgingDiff.difference}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                                    Spread
                                                </Typography>
                                            </Box>
                                        </StyledTableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Scrollbar>
        </CompactCard>
    );

    // Render mobile view - Compact flat row design
    const renderMobileView = () => (
        <Box sx={{ px: 0.5 }}>
            {/* Mobile Header */}
            {displayedWatchlist.length > 0 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    p: 1,
                    mb: 0.5,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}>
                    {editMode && (
                        <Checkbox
                            checked={selectedItems.length === displayedWatchlist.length && displayedWatchlist.length > 0}
                            indeterminate={selectedItems.length > 0 && selectedItems.length < displayedWatchlist.length}
                            onChange={handleSelectAll}
                            size="small"
                            sx={{ p: 0.25 }}
                        />
                    )}
                    <Typography variant="caption" sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'primary.main',
                        flex: 1
                    }}>
                        Script 1
                    </Typography>
                    <Typography variant="caption" sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        color: 'text.secondary',
                        textAlign: 'center',
                        minWidth: 40
                    }}>
                        Diff
                    </Typography>
                    <Typography variant="caption" sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: 'primary.main',
                        flex: 1,
                        textAlign: 'right'
                    }}>
                        Script 2
                    </Typography>
                </Box>
            )}

            {displayedWatchlist.map((item) => {
                const quote1 = getQuoteData(item.script1?.instrument_token, item.script1?.side);
                const quote2 = getQuoteData(item.script2?.instrument_token, item.script2?.side);
                const hedgingDiff = calculateHedgingDifference(item.script1, item.script2);

                return (
                    <MobileCard key={item.id}>
                        <CompactRow>
                            {editMode && (
                                <Checkbox
                                    checked={selectedItems.includes(item.id)}
                                    onChange={() => handleItemSelect(item.id)}
                                    size="small"
                                    sx={{ p: 0.25 }}
                                />
                            )}

                            {/* Script 1 - Compact */}
                            <ScriptCompact side={item.script1.side}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', mb: 0.25 }}>
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {modifyScriptName(item.script1.name, item.script1.expiry)}
                                    </Typography>
                                    {getSideChip(item.script1.side, true)}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                                    <Typography variant="body2" sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        color: getPriceColor(quote1.change),
                                        lineHeight: 1
                                    }}>
                                        {formatPrice(quote1.price)}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.55rem',
                                        fontWeight: 600,
                                        color: item.script1.side === 'BUY' ? 'success.dark' : 'error.dark',
                                        backgroundColor: item.script1.side === 'BUY' ? 'success.lighter' : 'error.lighter',
                                        px: 0.25,
                                        py: 0.1,
                                        borderRadius: 0.25,
                                        lineHeight: 1
                                    }}>
                                        {item.script1.side === 'BUY' ? 'BID' : 'ASK'}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.6rem',
                                        color: getPriceColor(quote1.change),
                                        lineHeight: 1
                                    }}>
                                        {quote1.changePercent > 0 ? '+' : ''}{quote1.changePercent.toFixed(1)}%
                                    </Typography>
                                </Box>
                            </ScriptCompact>

                            {/* VS Divider */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 0.5, minWidth: 40 }}>
                                <Typography variant="caption" sx={{
                                    fontSize: '0.6rem',
                                    color: 'text.secondary',
                                    fontWeight: 600
                                }}>
                                    VS
                                </Typography>
                                <Typography variant="caption" sx={{
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    color: hedgingDiff.color,
                                    lineHeight: 1,
                                    textAlign: 'center'
                                }}>
                                    {hedgingDiff.rawDifference >= 0 ? '+' : '-'}{hedgingDiff.difference}
                                </Typography>
                            </Box>

                            {/* Script 2 - Compact */}
                            <ScriptCompact side={item.script2.side}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', mb: 0.25 }}>
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        lineHeight: 1,
                                        flex: 1,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {modifyScriptName(item.script2.name, item.script2.expiry)}
                                    </Typography>
                                    {getSideChip(item.script2.side, true)}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                                    <Typography variant="body2" sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        color: getPriceColor(quote2.change),
                                        lineHeight: 1
                                    }}>
                                        {formatPrice(quote2.price)}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.55rem',
                                        fontWeight: 600,
                                        color: item.script2.side === 'BUY' ? 'success.dark' : 'error.dark',
                                        backgroundColor: item.script2.side === 'BUY' ? 'success.lighter' : 'error.lighter',
                                        px: 0.25,
                                        py: 0.1,
                                        borderRadius: 0.25,
                                        lineHeight: 1
                                    }}>
                                        {item.script2.side === 'BUY' ? 'BID' : 'ASK'}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.6rem',
                                        color: getPriceColor(quote2.change),
                                        lineHeight: 1
                                    }}>
                                        {quote2.changePercent > 0 ? '+' : ''}{quote2.changePercent.toFixed(1)}%
                                    </Typography>
                                </Box>
                            </ScriptCompact>
                        </CompactRow>
                    </MobileCard>
                );
            })}

            {/* Show pagination info for mobile if items are limited */}
            {isMobile && filteredWatchlist.length > displayedWatchlist.length && (
                <Box sx={{
                    textAlign: 'center',
                    p: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.04),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                    mt: 1
                }}>
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Showing {displayedWatchlist.length} of {filteredWatchlist.length} hedging pairs
                    </Typography>
                    <Typography variant="caption" sx={{
                        fontSize: '0.7rem',
                        color: 'info.main',
                        display: 'block',
                        mt: 0.5
                    }}>
                        Use search to find specific pairs
                    </Typography>
                </Box>
            )}
        </Box>
    );

    // Render main content with proper conditional logic
    const renderMainContent = () => {
        if (hasData) {
            return isMobile ? renderMobileView() : renderDesktopTable();
        }

        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Iconify
                    icon="mdi:chart-multiple"
                    sx={{
                        fontSize: 80,
                        color: 'text.disabled',
                        mb: 2
                    }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                    No Hedging Pairs Added
                </Typography>
                <Typography variant="body1" color="text.disabled" sx={{ mb: 3 }}>
                    Create your first hedging pair to start monitoring price differences
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={handleAddHedgingPair}
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                        }
                    }}
                >
                    Add Hedging Pair
                </Button>
            </Box>
        );
    };

    const hasData = hedgingWatchlist && hedgingWatchlist.length > 0;

    return (
        <Page title="Hedging Watchlist">
            <Container maxWidth={themeStretch ? false : 'lg'}>
                <HeaderBreadcrumbs
                    heading="Hedging Watchlist"
                    links={[
                        { name: 'Dashboard', href: `/${location.pathname.slice(1).split('/')[0]}` },
                    ]}
                    action={
                        <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 2} alignItems={isMobile ? "stretch" : "center"}>
                            {/* Search Bar - Only show if there's data */}
                            {hasData && (
                                <TextField
                                    placeholder="Search hedging pairs..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: <Iconify icon="eva:search-fill" sx={{ mr: 1, color: 'text.disabled' }} />,
                                    }}
                                    sx={{
                                        width: isMobile ? '100%' : 300,
                                        '& .MuiOutlinedInput-root': {
                                            fontSize: isMobile ? '0.85rem' : '0.875rem',
                                            height: isMobile ? 38 : 42,
                                        },
                                    }}
                                    size="small"
                                />
                            )}

                            {/* Action Buttons */}
                            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                                {hasData && (
                                    <Tooltip title={editMode ? 'Cancel' : 'Edit'} arrow>
                                        <Button
                                            variant={editMode ? "contained" : "outlined"}
                                            color={editMode ? "error" : "inherit"}
                                            startIcon={<Iconify icon={editMode ? "eva:close-fill" : "eva:edit-fill"} />}
                                            onClick={handleEditModeToggle}
                                            size="small"
                                            sx={{
                                                minWidth: isMobile ? 'auto' : undefined,
                                                px: isMobile ? 1 : undefined,
                                            }}
                                        >
                                            {!isMobile && (editMode ? 'Cancel' : 'Edit')}
                                        </Button>
                                    </Tooltip>
                                )}
                                {editMode && selectedItems.length > 0 && (
                                    <Tooltip title={`Delete ${selectedItems.length} items`} arrow>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            startIcon={<Iconify icon="eva:trash-2-fill" />}
                                            onClick={handleDeleteSelected}
                                            size="small"
                                            sx={{
                                                minWidth: isMobile ? 'auto' : undefined,
                                                px: isMobile ? 1 : undefined,
                                            }}
                                        >
                                            {!isMobile && `Delete (${selectedItems.length})`}
                                        </Button>
                                    </Tooltip>
                                )}
                                <Tooltip title="Add Hedging Pair" arrow>
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="eva:plus-fill" />}
                                        onClick={handleAddHedgingPair}
                                        size="small"
                                        sx={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                            },
                                            minWidth: isMobile ? 'auto' : undefined,
                                            px: isMobile ? 1 : undefined,
                                        }}
                                    >
                                        {!isMobile && 'Add Hedging Pair'}
                                    </Button>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    }
                />

                {renderMainContent()}

                {/* Floating Add Button for Mobile */}
                {isMobile && hasData && (
                    <Fab
                        color="primary"
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            }
                        }}
                        onClick={handleAddHedgingPair}
                    >
                        <Iconify icon="eva:plus-fill" />
                    </Fab>
                )}

                {/* Add Modal */}
                <HedgingWatchlistModal
                    open={showAddModal}
                    onClose={handleCloseModal}
                />

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete {selectedItems.length} selected item(s)? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button onClick={confirmDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Page>
    );
};

export default HedgingWatchList;
export { HedgingWatchList };